import { authService } from './authService'
import { getTenantId } from '../utils/tenantUtils'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

const BASE = '/api/v1/prediction'

export type PredictionPaymentType = 'cash' | 'points'

export interface PredictionSelection {
  _id: string
  selectionCode: string
  label: string
  subtitle?: string
  currentOdds: number
  oddsVersion: number
  badgeLabel?: string
  barColor?: string
  cornerSide?: 'red' | 'blue' | 'neutral' | null
  imageUrl?: string | null
  matchId?: string | null
}

export interface PredictionMatch {
  _id: string
  matchCode: string
  label?: string
  displayOrder?: number
  status: 'open' | 'closed' | 'settled' | 'void'
  winningSelectionId?: string | null
  red?: PredictionSelection
  blue?: PredictionSelection
  selections?: PredictionSelection[]
}

export interface PredictionEvent {
  _id: string
  eventCode: string
  title: string
  description?: string
  category: string
  marketLayout?: 'flat' | 'versus_pairs'
  paymentMode?: string
  status: string
  minStake?: number
  maxStake?: number
  maxStakePerUser?: number
  allowedPaymentTypes?: PredictionPaymentType[]
  displayConfig?: {
    graphicMode?: 'auto' | 'election' | 'versus_promo' | 'versus_matchup'
    matchupFocus?: string
    backgroundImageUrl?: string | null
    promoBackgroundImageUrl?: string | null
    matchupBackgroundImageUrl?: string | null
    backgroundOverlayOpacity?: number
  } | null
  prizeInfo?: {
    headline?: string
    description?: string
    rewardTypes?: string[]
    extraNote?: string
  } | null
  rewardProfile?: {
    profileMode?: string
    monetaryEnabled?: boolean
    prizes?: Array<{
      prizeId: string
      name: string
      description?: string
      quantity: number
      allocationRule: string
      enabled?: boolean
    }>
    promoHeadline?: string
    promoDescription?: string
    promoExtraNote?: string
  } | null
  selections?: PredictionSelection[]
  matches?: PredictionMatch[]
  popularity?: {
    totalStake: number
    totalBets: number
    selections: Array<{
      selectionId: string
      label: string
      badgeLabel?: string
      barColor: string
      imageUrl?: string | null
      currentOdds: number
      stakePercent: number
      totalStake: number
      isFavorite: boolean
    }>
  }
}

export interface PredictionBetSlip {
  slipId: string
  eventTitle?: string
  selectionLabel?: string
  stake: number
  lockedOdds: number
  potentialPayout: number
  paymentType: PredictionPaymentType
  status: string
  payoutAmount?: number
  createdAt: string
}

async function predictionFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = authService.getToken()
  const tenantId = getTenantId()
  const res = await fetch(withApiBaseUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
    credentials: 'include',
    cache: 'no-store',
  })

  const data = await res.json().catch(() => ({ success: false, message: 'Invalid response' }))
  if (!res.ok || data.success === false) {
    throw new Error(data.message || data.error || `Request failed (${res.status})`)
  }
  return data as T
}

export async function listOpenEvents(forceRefresh = false) {
  const suffix = forceRefresh ? `?t=${Date.now()}` : ''
  return predictionFetch<{ success: boolean; data: PredictionEvent[]; meta?: { userEntryVisible?: boolean } }>(`${BASE}/events${suffix}`)
}

export async function getEventDetail(eventId: string) {
  return predictionFetch<{ success: boolean; data: PredictionEvent }>(`${BASE}/events/${eventId}`)
}

export async function placeBet(payload: {
  eventId: string
  selectionId: string
  stake: number
  paymentType: PredictionPaymentType
}) {
  return predictionFetch<{ success: boolean; data: { slip: PredictionBetSlip; newBalance: number; paymentType: string } }>(
    `${BASE}/bets`,
    { method: 'POST', body: JSON.stringify(payload) }
  )
}

export async function getBetHistory(params?: {
  eventId?: string
  status?: string
  limit?: number
  skip?: number
  maxHistory?: number
}) {
  const search = new URLSearchParams()
  if (params?.eventId) search.set('eventId', params.eventId)
  if (params?.status) search.set('status', params.status)
  if (params?.limit !== undefined) search.set('limit', String(params.limit))
  if (params?.skip !== undefined) search.set('skip', String(params.skip))
  if (params?.maxHistory !== undefined) search.set('maxHistory', String(params.maxHistory))
  const qs = search.toString()
  return predictionFetch<{
    success: boolean
    data: PredictionBetSlip[]
    total?: number
    limit?: number
    skip?: number
    maxHistory?: number
  }>(
    `${BASE}/bets/history${qs ? `?${qs}` : ''}`
  )
}
